import * as React from "react";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { Checkbox } from "@mui/material";
import { TextFieldSolid, TextAreaSolid } from "@iventis/styles/src/components/text-field-solid";
import { TimeRange, DataFieldValueTypes, TimeSpan, MapObjectRepeatedTimeRangeValue } from "@iventis/types";
import { StyledFieldLabelLight, inputHeight, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { convertAreaMeasurement, convertLengthMeasurement } from "@iventis/utilities";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { AttributeTimeRangeEditor } from "@iventis/datafield-editor";
import { DateSelectorComponent, HyperlinkInput, LoadingComponent, TimeSelector } from "@iventis/components";
import { NumberInputComponent } from "@iventis/components/src/number-input";
import { preventMouseWheelInput } from "@iventis/utilities/src/input/number-input-helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Asset } from "@iventis/domain-model/model/asset";
import { Content } from "@iventis/translations";
import { toast } from "@iventis/toasts/src/toast";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { AreaModelRotation } from "@iventis/domain-model/model/areaModelRotation";
import { getStaticStyleValue } from "@iventis/layer-style-helpers/src/create-style-value-helpers";
import { ListDataFieldSelector } from "./object-data-field-list";
import { MULTIPLE_VALUES_LABEL } from "./object-details-constants";
import { ObjectDetailsDataFieldImage } from "./object-details-data-field-image";
import { ObjectDetailsDataFieldRepeatedTimeRangeComponent } from "./object-details-data-field-repeated-time-range";

const MAX_QUANTITY_OF_MODELS_IN_AREA_VALUE = 1000;

export const ObjectDataField: React.FunctionComponent<{
    dataField: DataField;
    systemDataFields: Record<SystemDataFieldName, DataField>;
    values: DataFieldValueTypes[];
    valueChanged: (value: DataFieldValueTypes) => void;
    disabled?: boolean;
    unitOfMeasurement: UnitOfMeasurement;
    relationshipValues: Record<string, unknown[]>;
    mapObjectName: string;
    layer: MapLayer;
    onEditAttributeSettingsClicked: (e: React.MouseEvent) => void;
    getAsset: (assetId: string) => Promise<Asset>;
    canUserEdit?: boolean;
}> = ({
    dataField,
    systemDataFields,
    values,
    valueChanged,
    disabled = false,
    unitOfMeasurement,
    relationshipValues,
    mapObjectName,
    layer,
    onEditAttributeSettingsClicked,
    getAsset,
    canUserEdit = true,
}) => {
    const translate = useIventisTranslate();
    const translatedName = dataField.systemDataFieldName == null ? dataField.name : translate(Content.map10.systemDataFieldNames[dataField.systemDataFieldName]);
    const areaDataFieldId = systemDataFields[SystemDataFieldName.MapObjectArea]?.id;
    const lengthDataFieldId = systemDataFields[SystemDataFieldName.MapObjectLength]?.id;
    const coordinateDataFieldId = systemDataFields[SystemDataFieldName.Coordinates]?.id;
    const routeTimeDataFieldId = systemDataFields[SystemDataFieldName.RouteTime]?.id;
    const quantityOfModelsOnALineDataFieldId = systemDataFields[SystemDataFieldName.QuantityOfModelsOnLine]?.id;
    const quantityOfModelsInAnAreaDataFieldId = systemDataFields[SystemDataFieldName.QuantityOfModelsInArea]?.id;
    const rotationZOfModelsInAreaDataFieldId = systemDataFields[SystemDataFieldName.RotationZOfModelsInArea]?.id;
    const iconRotationDataFieldId = systemDataFields[SystemDataFieldName.IconRotation]?.id;
    const altitudeDataFieldId = systemDataFields[SystemDataFieldName.ModelAltitude]?.id;

    // Gets value to display in the input boxes
    const [value, allValuesIdentical] = React.useMemo(() => {
        const allValuesIdentical = values.every((value) => value === values[0]);
        return [allValuesIdentical ? values[0] : MULTIPLE_VALUES_LABEL, allValuesIdentical];
    }, [values]);

    if (dataField?.id && (dataField.id === areaDataFieldId || dataField.id === lengthDataFieldId) && typeof value === "number") {
        const displayValue = dataField.id === areaDataFieldId ? convertAreaMeasurement(value, unitOfMeasurement) : convertLengthMeasurement(value, unitOfMeasurement);
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <NonEditableTextInput type="text" value={displayValue} disabled={disabled} data-testid={`attribute-value-${dataField.name}`} />
            </>
        );
    }

    if (dataField?.id && dataField.id === coordinateDataFieldId && typeof value === "string") {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <NonEditableTextInput type="text" value={value} disabled={disabled} data-testid={`attribute-value-${dataField.name}`} />
            </>
        );
    }

    if (dataField?.id && dataField.id === routeTimeDataFieldId) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <TimeSelector name={dataField.name} disabled={disabled} value={value as TimeSpan} dataTestId={`attribute-value-${dataField.name}`} />
            </>
        );
    }

    if (dataField?.id && dataField.id === quantityOfModelsOnALineDataFieldId) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <NonEditableTextInput name={dataField.name} disabled={disabled} value={value as TimeSpan} data-testid={`attribute-value-${dataField.name}`} />
            </>
        );
    }

    // If the data field is the rotation Z of models in an area and the rotation type is random, the rotation cannot be edited
    if (dataField?.id && dataField.id === rotationZOfModelsInAreaDataFieldId) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <NumberInputComponent
                    value={
                        typeof value === "number" || typeof value === "string"
                            ? getStaticStyleValue(layer?.areaModelStyle?.rotationType) === AreaModelRotation.Random
                                ? translate(Content.map10.areaModel.rotation.random)
                                : value
                            : 0
                    }
                    onValidValueInputted={(value) => valueChanged(value)}
                    disabled={disabled}
                    testId={`attribute-value-${dataField.name}`}
                    maxValue={360}
                    minValue={0}
                />
            </>
        );
    }

    if (dataField.type === DataFieldType.What3Words) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">What3Words</StyledFieldLabelLight>
                <NonEditableTextInput
                    value={values.length === 1 ? value ?? "" : MULTIPLE_VALUES_LABEL}
                    type="text"
                    data-testid="attribute-value-what3words"
                    fullWidth
                    disabled
                    startAdornment={
                        // If what3words value is null and only one object is selected, show loading spinner
                        <>
                            {value == null && values.length === 1 && (
                                <TextFieldLoadingComponent>
                                    <LoadingComponent size="80%" data-testid="What3Words-loading-spinner" />
                                </TextFieldLoadingComponent>
                            )}
                        </>
                    }
                />
            </>
        );
    }

    if (dataField.id === quantityOfModelsInAnAreaDataFieldId) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <NumberInputComponent
                    maxValue={MAX_QUANTITY_OF_MODELS_IN_AREA_VALUE}
                    minValue={1}
                    value={typeof value === "number" ? value : 1}
                    onValidValueInputted={(value) => valueChanged(value)}
                    disabled={disabled}
                    testId={`attribute-value-${dataField.name}`}
                />
            </>
        );
    }

    if (dataField.id === iconRotationDataFieldId) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                <NumberInputComponent
                    decimalPlaces={2}
                    value={typeof value === "number" || typeof value === "string" ? value : 0}
                    onValidValueInputted={(value) => valueChanged(value)}
                    disabled={disabled}
                    testId={`attribute-value-${dataField.name}`}
                    maxValue={360}
                    minValue={0}
                />
            </>
        );
    }

    if (dataField.id === altitudeDataFieldId) {
        return (
            <>
                <StyledFieldLabelLight className="object-data-field-label">{`${translatedName} (${translate(Content.map3.units.meters)})`}</StyledFieldLabelLight>
                <NumberInputComponent
                    decimalPlaces={2}
                    value={typeof value === "number" || typeof value === "string" ? value : 0}
                    onValidValueInputted={(value) => valueChanged(value)}
                    disabled={disabled}
                    testId={`attribute-value-${dataField.name}`}
                    minValue={0}
                    maxValue={50}
                />
            </>
        );
    }

    return (
        <>
            {dataField.type === DataFieldType.List && (
                <ListDataFieldSelector
                    dataField={dataField}
                    listItemIds={values as string[]}
                    onChange={(listItemId) => valueChanged(listItemId)}
                    disabled={disabled}
                    relationshipValues={relationshipValues}
                />
            )}
            {dataField.type === DataFieldType.Number && (
                <>
                    <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                    <TextFieldSolid
                        // Change input type to show "Mutliple" can't be shown when type is number
                        type={disabled && values.length > 1 ? "text" : "number"}
                        value={value || ""}
                        onChange={(event) => valueChanged(Number(event.target.value))}
                        disabled={disabled}
                        data-testid={`attribute-value-${dataField.name}`}
                        fullWidth
                        onWheel={preventMouseWheelInput}
                    />
                </>
            )}
            {dataField.type === DataFieldType.Text && (
                <>
                    <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                    <TextAreaSolid
                        value={typeof value === "string" ? value : ""}
                        minRows={1}
                        maxRows={4}
                        onChange={(event) => valueChanged(event.target.value)}
                        disabled={disabled}
                        data-testid={`attribute-value-${dataField.name}`}
                    />
                </>
            )}
            {dataField.type === DataFieldType.Tickbox && (
                <StyledTickBoxDataFieldValue>
                    <Checkbox
                        sx={{ padding: "0px", paddingRight: "5px" }}
                        checked={allValuesIdentical ? (value as boolean) || false : false}
                        onChange={(event) => valueChanged(event.target.checked)}
                        color="primary"
                        data-testid={`attribute-value-${dataField.name}`}
                        disabled={disabled}
                    />
                    <StyledFieldLabelLight sx={{ marginBottom: "0px !important" }} className="object-data-field-label">
                        {translatedName}
                    </StyledFieldLabelLight>
                </StyledTickBoxDataFieldValue>
            )}
            {dataField.type === DataFieldType.Date && (
                <>
                    <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                    <DateSelectorComponent value={value as Date} valueChanged={valueChanged} disabled={disabled} testId={`attribute-value-${dataField.name}`} />
                </>
            )}
            {dataField.type === DataFieldType.Time && (
                <>
                    <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                    <TimeSelector
                        name={dataField.name}
                        onChange={(event) => valueChanged(event.target.value)}
                        value={value as TimeSpan}
                        dataTestId={`attribute-value-${dataField.name}`}
                    />
                </>
            )}
            {dataField.type === DataFieldType.TimeRange && (
                <>
                    <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                    <AttributeTimeRangeEditor className="time-range-selector" value={value as TimeRange} setValue={valueChanged} testIdPrefix={dataField.name} />
                </>
            )}
            {dataField.type === DataFieldType.Hyperlink && (
                <>
                    <StyledFieldLabelLight className="object-data-field-label">{translatedName}</StyledFieldLabelLight>
                    <HyperlinkInput
                        value={value as string}
                        onValueChange={(value) => valueChanged(value)}
                        debounceTime={1000}
                        onClickLinkError={() => {
                            toast.error({ title: translate(Content.errors.error), message: translate(Content.errors.invalidLink) });
                        }}
                        testId={dataField.name}
                    />
                </>
            )}
            {dataField.type === DataFieldType.Image && values.length < 2 && (
                <ObjectDetailsDataFieldImage name={translatedName} dataFieldValue={value as string} getImage={getAsset} valueChanged={valueChanged} />
            )}
            {dataField.type === DataFieldType.RepeatedTimeRanges && values.length < 2 && (
                <ObjectDetailsDataFieldRepeatedTimeRangeComponent
                    name={translatedName}
                    dataFieldValue={value as MapObjectRepeatedTimeRangeValue[]}
                    valueChanged={valueChanged}
                    mapObjectName={mapObjectName}
                    layerName={layer?.name}
                    onAttributeSettingsClicked={onEditAttributeSettingsClicked}
                    canUserEdit={canUserEdit}
                />
            )}
        </>
    );
};

const TextFieldLoadingComponent = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${inputHeight};
    height: ${inputHeight};
    z-index: 1;
`;

const NonEditableTextInput = styled(TextFieldSolid)`
    position: relative;
    // Override the disabled colour
    .Mui-disabled {
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core} !important;
        -webkit-text-fill-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core} !important;
    }
`;

const StyledTickBoxDataFieldValue = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
`;
